import React from 'react';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Container, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import { attributionPillSx, textLinkSx } from './CaseStudies';
import CaseStudyNextSteps from '../components/CaseStudyNextSteps';
import PageHero from '../components/stitch/PageHero';
import { cardSx } from '../components/stitch/Section';
import { caseStudyPath } from '../data/CaseStudies';
import { InsightArticle, insightPath, insights } from '../data/Insights';
import { tokens } from '../theme';

// Built to the Stitch "Insights" screen (project 1587770953174176333): the first article as a
// wide featured card, the rest in a two-column grid, each keeping its summary and related study.
const number = (index: number) => String(index + 1).padStart(2, '0');

function ArticleCard({ article, index }: { article: InsightArticle; index: number }) {
  const featured = index === 0;
  return (
    <Box
      component='article'
      aria-labelledby={`${article.id}-title`}
      sx={{
        ...cardSx,
        minWidth: 0,
        p: { xs: 3, md: featured ? 5 : 4 },
        display: 'flex',
        flexDirection: 'column',
        gridColumn: { md: featured ? '1 / -1' : 'auto' },
        ...(featured && { borderTop: `4px solid ${tokens.secondary}` }),
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
        <Typography
          variant='labelCaps'
          component='p'
          aria-hidden='true'
          sx={{ color: tokens.accentText }}
        >
          Article {number(index)}
        </Typography>
        <Typography
          variant='labelCaps'
          component='p'
          sx={attributionPillSx}
        >
          {article.topic}
        </Typography>
      </Box>
      <Typography
        component='h2'
        id={`${article.id}-title`}
        variant={featured ? 'h3' : 'h4'}
        aria-label={`${article.title} — ${article.topic}`}
        sx={{ mt: 2 }}
      >
        {article.title}
      </Typography>
      <Typography
        variant='body2'
        sx={{ mt: 1.5, maxWidth: '72ch' }}
      >
        {article.summary}
      </Typography>
      <Box
        sx={{
          mt: 2.5,
          p: 2,
          borderRadius: 2,
          bgcolor: tokens.surfaceLow,
        }}
      >
        <Typography
          variant='labelCaps'
          component='p'
          sx={{ color: tokens.accentText }}
        >
          Related case {article.relatedCaseStudies.length === 1 ? 'study' : 'studies'}
        </Typography>
        <Box
          component='ul'
          sx={{ listStyle: 'none', m: 0, mt: 1, p: 0, display: 'grid', gap: 0.75 }}
        >
          {article.relatedCaseStudies.map((study) => (
            <li key={study.studyId}>
              <Typography
                variant='bodySm'
                component='p'
                sx={{ color: tokens.onSurfaceVariant }}
              >
                <Box
                  component={Link}
                  to={caseStudyPath(study.studyId)}
                  sx={{ color: tokens.primary, fontWeight: 600 }}
                >
                  {study.title}
                </Box>{' '}
                ({study.attribution})
              </Typography>
            </li>
          ))}
        </Box>
      </Box>
      <Box
        component={Link}
        to={insightPath(article.id)}
        aria-label={`Read the article: ${article.title}`}
        sx={{ ...textLinkSx, mt: 'auto', pt: 3, alignSelf: 'flex-start' }}
      >
        Read the article
        <ArrowForwardIcon
          aria-hidden='true'
          sx={{ fontSize: 18 }}
        />
      </Box>
    </Box>
  );
}

function Insights() {
  return (
    <Container sx={{ '& .MuiTypography-root': { overflowWrap: 'anywhere' } }}>
      <PageHero
        eyebrow='Practical insights'
        title='CMS and software insights'
        intro='Practical guides for teams planning multi-site content platforms, policy and handbook approval workflows, multilingual publishing, accessible authoring, and custom software. Each article connects common buyer questions to delivered case studies and our CMS capability statement.'
      />

      <Box
        component='nav'
        aria-label='Insights index'
        sx={{ ...cardSx, p: { xs: 3, md: 4 }, maxWidth: '56rem' }}
      >
        <Typography
          component='h2'
          variant='h5'
        >
          Explore the articles
        </Typography>
        <Box
          component='ol'
          sx={{ listStyle: 'none', m: 0, mt: 2, p: 0, display: 'grid', gap: 1 }}
        >
          {insights.map((article, index) => (
            <Box
              component='li'
              key={article.id}
              sx={{ minWidth: 0 }}
            >
              <Box
                component={Link}
                to={insightPath(article.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: tokens.surfaceLow,
                  color: tokens.primary,
                  fontSize: '0.875rem',
                  lineHeight: 20 / 14,
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&:hover': { bgcolor: tokens.surfaceContainer, textDecoration: 'underline' },
                }}
              >
                <Box
                  component='span'
                  sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
                >
                  <span aria-hidden='true'>{number(index)} </span>
                  {article.title}
                </Box>
                <ArrowForwardIcon
                  aria-hidden='true'
                  sx={{ fontSize: 18, color: tokens.outline, flexShrink: 0 }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          mt: { xs: 5, md: 8 },
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 3,
        }}
      >
        {insights.map((article, index) => (
          <ArticleCard
            key={article.id}
            article={article}
            index={index}
          />
        ))}
      </Box>

      <CaseStudyNextSteps />
    </Container>
  );
}

export default Insights;
